'use strict'

// The Vue entry point. This was `app.js` the Angular bootstrap until step 41 of #614; the app is
// Vue only now, so there is no `angular.module`, no ui-router, no `$http` interceptor and no
// `vue-services` bridge. What is left is the vendor setup, the axios token/401 handling, the boot
// session check and the mount.

import axios from 'axios'
import moment from 'moment-timezone'

import { createApp } from 'vue'
import VCalendar from 'v-calendar'
import DOMPurify from 'dompurify'

import store from './store'
import { router, isAuthPath } from './routes.js'
import App from './App.vue'
import { report as reportLoadMetrics } from './loadMetrics.js'
import userStore from './common/service/userStore.js'
import jobPrefix from './common/service/jobPrefix.js'
import currency from './common/service/currency.js'
import loginService from './login/service/loginService.js'
import resourcesService from './resources/service/resourcesService.js'

import { Amplify } from 'aws-amplify'
import { fetchAuthSession } from 'aws-amplify/auth'
import { completeIdpLogout } from './login/service/idpLogout.js'

// side-effect imports the app relies on
import './jqueryGlobal.js' // exposes window.jQuery / window.$ for metismenu
import 'metismenu'
import 'tippy.js/dist/tippy.css'

// css dependencies
import '../node_modules/bootstrap/dist/css/bootstrap.css'
import '../node_modules/font-awesome/css/font-awesome.css'
import '../node_modules/animate.css/animate.css'
import 'v-calendar/style.css'
import '../node_modules/metismenu/dist/metisMenu.css'
import '../node_modules/typeface-open-sans/index.css'
import './profile/views/profile.css'
import './web/css/style.css'
import './app.css'

import authConfig from './common/service/authConfig.js'

// Monday is the first day of the week. Was in the Angular config block; it is a moment setting, so
// it belongs with the moment import.
moment.locale('en_au', { week: { dow: 1 } })

const app = createApp(App)

// Every component gets the job prefix, so a template writes `{{ jobPrefix }}{{ job.jobNumber }}`
// rather than each one reaching for the service. It is reactive, so a value that loads after mount
// still updates the view.
app.mixin({
  computed: {
    jobPrefix() {
      return jobPrefix.state.prefix
    },
  },
})

// VCalendar has a Vue 3 release; the other Vue 2 plugins (VueSocketIO, ToggleButton, BootstrapVue,
// Vue2Filters, VueScrollTo) do not and are removed here, so the app boots under the migration build.
// Their components are handled in the library phase of #615.
app.use(VCalendar, {})
// Vue 3 removed filters, so `currency` is a global property the templates call directly
// (`{{ $currency(x, '$', 0) }}`) rather than a `| currency` filter.
app.config.globalProperties.$currency = currency
app.config.devtools = false

// One anonymous page-load sample per browser session, for the diagnostics dashboard (#652).
// Started early so the LCP observer sees the whole load; it sends itself on pagehide.
reportLoadMetrics()

// vue-sanitize is a Vue 2 plugin; the one thing the app used from it is `$sanitize`, which becomes a
// global property over DOMPurify directly.
app.config.globalProperties.$sanitize = (html, options) => DOMPurify.sanitize(html, options || { allowedTags: ['br'] })

import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faCircleXmark,
  faCaretDown,
  faCaretUp,
  faClock,
  faBriefcase,
  faArrowRight,
  faArrowDown,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

library.add(faCaretDown)
library.add(faCaretUp)
library.add(faCircleXmark)
library.add(faClock)
library.add(faBriefcase)
library.add(faArrowRight)
library.add(faArrowDown)
library.add(faXmark)
app.component('font-awesome-icon', FontAwesomeIcon)

// The Cognito token on every request. Was `$http`'s `httpRequestInterceptor` (the request half);
// the axios instance is shared, so every migrated service gets it.
axios.interceptors.request.use(
  async config => {
    // v6's fetchAuthSession throws when there is no session (the sign-in request itself), so an
    // unauthenticated request must not fail here.
    try {
      const session = await fetchAuthSession()
      if (session && session.tokens && session.tokens.idToken) {
        config.headers['authorization'] = 'Bearer ' + session.tokens.idToken.toString()
      }
    } catch (error) {
      // no session yet - send the request without an authorization header
    }
    return config
  },
  function (error) {
    console.error('Error sending token with axios', error, error.stack)
    router.push({ name: 'login', query: { redirect: window.location.pathname } }).catch(() => {})
  }
)

// The 401 half of `httpRequestInterceptor`'s responseError, kept exactly:
//   * only an authentication failure means the session is gone - bouncing on *any* error logged
//     the user out over a transient 400/500.
//   * only bounce when a token was actually attached - on a fresh load the token store can still
//     be settling, the request goes out unauthenticated and 401s, which is not an expired session.
// One 401 usually means several - a page load fires a handful of requests at once - so the expiry
// is done once and the rest are ignored while it happens.
let expiring = false

axios.interceptors.response.use(
  response => response,
  error => {
    const attachedToken = error && error.config && error.config.headers && error.config.headers.authorization
    // 403 with "Error verifying user" is what the API answered before it was corrected to 401. A
    // tab left open across a deploy can still receive it, and treating it as anything other than an
    // expired session leaves the app stuck with every request refused.
    const staleAuth =
      error &&
      error.response &&
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.message === 'Error verifying user'
    const authenticationFailed = error && error.response && error.response.status === 401

    if (attachedToken && !expiring && (authenticationFailed || staleAuth)) {
      expiring = true
      // End the session, then route. Ending it matters: without it the next request refreshes the
      // access token silently and the user carries on, which is what made a 30-minute token
      // meaningless (#671 step 2b).
      loginService
        .expireSession()
        .catch(() => {})
        .then(() => {
          expiring = false
          router.push({ name: 'login', query: { redirect: window.location.pathname } }).catch(() => {})
        })
    }
    return Promise.reject(error)
  }
)

// Shown when the server cannot tell us which pool to use. Deliberately in the page rather than the
// console: the alternative is what this used to do - configure Amplify against the baked OLD-pool
// config, where MFA is off, and sign people in with a weaker policy and no MFA prompt (#671).
const showAuthConfigProblem = () => {
  const existing = document.getElementById('auth-config-problem')
  if (existing) return

  const message = document.createElement('div')
  message.id = 'auth-config-problem'
  message.setAttribute('role', 'alert')
  message.style.cssText =
    'position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;' +
    'background:#22303c;color:#fff;font:16px/1.5 sans-serif;text-align:center;padding:2rem;'
  message.textContent = 'Cannot reach Hourglass right now. Retrying…'
  document.body.appendChild(message)
}

// The Cognito config comes from the server (#671 A2.2d) so the pool can be switched or reverted
// without a rebuild. Amplify must be configured before any auth call, so the boot waits for it.
const startApp = async () => {
  const loaded = await authConfig.load()
  if (!loaded) {
    showAuthConfigProblem()
    setTimeout(startApp, 2000)
    return
  }

  // Returning from Cognito's logout with a company identity provider to end as well (#677): finish
  // that, then come back here for an ordinary page load.
  if (completeIdpLogout()) {
    return
  }

  const config = authConfig.get()
  const problem = document.getElementById('auth-config-problem')
  if (problem) problem.remove()

  Amplify.configure({
    Auth: {
      Cognito: {
        identityPoolId: config.identityPoolId,
        userPoolId: config.userPoolId,
        userPoolClientId: config.clientId,
        signUpVerificationMethod: 'code',
        // Absent unless the server names a Hosted UI domain (#677). Without it Amplify refuses a
        // redirect sign-in with "OAuth not configured", so the login page never offers the button
        // in that case - which is every environment until one says otherwise.
        ...(config.oauth ? { loginWith: { oauth: config.oauth } } : {}),
      },
    },
  })

  // Kick off the incomplete-bookings count the menu shows. Here rather than inside the module, so
  // it runs after the boot rather than the moment the file is imported - the session has to be
  // restored before `loginService.currentSession()`'s retry window matters.
  resourcesService.start()

  // The boot session check. LoginCtrl used to do this on every page load as the layout controller:
  // set the user from the session, or send the visitor to login. A failure on an auth path is fine
  // - the guard below renders it.
  loginService.currentSession().then(
    session => {
      userStore.setUser(session.tokens.idToken.payload)
      // After the session, so the request is authenticated; the value is presentational, so a view
      // that renders first simply shows the default until this resolves.
      return jobPrefix.load()
    },
    () => {
      const path = window.location.pathname
      if (!isAuthPath(path)) {
        router.push({ name: 'login', query: { redirect: path } }).catch(() => {})
      }
    }
  )

  app.use(router)
  app.use(store)
  app.mount('#app')
}

startApp()
